import { PURCHASE_SOURCE_PLATFORM_MAP } from "@shared/constants/enums";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { getPlatform } from "@shared/utils/get-platform";
import { checkMobileWebviewActive } from "@shared/utils/platform/react-native";

/**
 * Returns the proper purchase platform for Box Office
 * depending on the application environment.
 * Should be used solely for the *_BOX_OFFICE platforms, for other platforms that are also used
 * in box office, such as Square, @see {@link getSquareTerminalPurchasePlatform}
 */
export function getBoxOfficePurchasePlatform() {
  const platform = getPlatform();
  switch (platform) {
    case "WEB":
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB_BOX_OFFICE;
    case "DESKTOP":
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_DESKTOP_BOX_OFFICE;
    case "MOBILE":
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_MOBILE_BOX_OFFICE;
    default:
      throw new Error(`Unsupported platform: ${platform}`);
  }
}

/**
 * Returns the proper purchase platform for public-facing pages.
 * Includes logic for widget displays.
 */
export function getPublicPurchasePlatform() {
  // In react native webview, set purchase platform to PLATFORM_MOBILE
  if (checkMobileWebviewActive())
    return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_MOBILE.id;
  const platform = getPlatform();
  switch (platform) {
    case "WEB":
      if (typeof window === "undefined") {
        return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id;
      }
      return getEmbeddedPurchasePlatform();
    case "DESKTOP":
      recordAppError(
        new Error("getPublicPurchasePlatform called on DESKTOP platform")
      );
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id;
    default:
      throw new Error(`Unsupported platform: ${platform}`);
  }
}

/**
 * Returns the proper purchase platform for Square Terminal *Oauth*
 * depending on the application environment
 */
export function getSquareTerminalPurchasePlatform() {
  const platform = getPlatform();
  switch (platform) {
    case "WEB":
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_TERMINAL_OAUTH;
    case "MOBILE":
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_TERMINAL_OAUTH_MOBILE;
    case "DESKTOP":
      return PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_TERMINAL_OAUTH_DESKTOP;
    default:
      throw new Error(`Unsupported platform: ${platform}`);
  }
}

/**
 * Returns true when a purchase_source_platform is a square terminal purchase platform
 */
export function isSquareTerminalPurchasePlatform(
  purchase_source_platform: string
) {
  return (
    purchase_source_platform ===
      PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_TERMINAL_OAUTH.id ||
    purchase_source_platform ===
      PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_TERMINAL_OAUTH_DESKTOP.id ||
    purchase_source_platform ===
      PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_TERMINAL_OAUTH_MOBILE.id
  );
}

/**
 * Returns true when a purchase_source_platform is a square kiosk purchase platform
 */
export function isSquareKioskPurchasePlatform(
  purchase_source_platform: string
) {
  return (
    purchase_source_platform ===
    PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_SQUARE_KIOSK.id
  );
}

export type BasketTerms = {
  id: number;
  name: string;
  itemGroupIds: Array<number>;
  hasAcceptedTerms: boolean;
  parent_event?: number;
  require_terms_acceptance?: boolean | null;
  terms_url_link?: string | null;
};
